import { ExtendServicePath, ExtendSubdomainService, apiPath, gatewayApiPath } from 'shared/models/service';
import { getDevsiteFullPath } from 'shared/utils/devsite';
import { getSubdomainServiceUrl } from 'shared/utils/subdomainServiceUrl';
import { BFF_ENABLED } from 'config/toggles';
import DevsiteEnv from 'modules/core/DevsiteEnv';
import { shouldUseGatewayAuth } from 'modules/core/auth/authMode';
import { session } from 'modules/core/auth/session';
import { HeaderKeys, HttpClientResponse, MimeType, QueryParameters, RequestHeaders } from './Definitions';
import { RequestExecutor, RequestExecutorOptions } from './RequestExecutor';
import { getBaseUrl } from './utils';

export class RegionalRequestExecutor<T extends string> extends RequestExecutor<T> {
  protected apiPath: ExtendServicePath;

  constructor(apiPath: ExtendServicePath, options: RequestExecutorOptions = {}) {
    super('', {
      defaultHeaders: { [HeaderKeys.CONTENT_TYPE]: MimeType.JSON },
      ...options,
      // If not proxying, we don't need to send csrf tokens for apis. They're cross domain and don't have a csrf vuln.
      // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
      csrfToken: BFF_ENABLED ? session.getCsrfToken : null,
    });
    this.apiPath = apiPath;
  }

  async fullWebSocketUrl(path: T): Promise<string> {
    const baseUrl = await getServiceEndpoint(this.apiPath, true);
    return `${baseUrl}${path}`;
  }

  async fullUrlAsync(path: T): Promise<string> {
    const baseUrl = await getServiceEndpoint(this.apiPath);
    return `${baseUrl}${path}`;
  }

  protected async execute(
    url: string,
    params: Partial<RequestInit>,
    headers?: RequestHeaders,
    queryParams?: QueryParameters,
    init?: RequestInit
  ): Promise<HttpClientResponse> {
    const sessionInfo = await session.getInfo();
    const baseUrl = await getServiceEndpoint(this.apiPath);
    const gatewayCredentials =
      shouldUseGatewayAuth(sessionInfo.platform) && !isNeverProxyPath(this.apiPath)
        ? { credentials: 'include' as const }
        : {};
    return super.execute(`${baseUrl}${url}`, { ...params, ...gatewayCredentials }, headers, queryParams, {
      ...init,
      ...gatewayCredentials,
    });
  }
}

// Direct tenant/robot hosts only — never BFF-proxy and never ODP developer-gateway rewrite
const neverProxyList: ExtendServicePath[] = [ExtendServicePath.ROBOT];

const isNeverProxyPath = (servicePath: string): boolean => neverProxyList.includes(servicePath as ExtendServicePath);

/**
 * Retrieves the regional service endpoint for an extend service.
 *
 * If devsite is in bff proxy mode, all endpoints will be resolved to the correct proxy.
 * @param servicePath the path of the extend service
 * @param isWebsocket whether to resolve a websocket endpoint or not
 * @returns
 */
export const getServiceEndpoint = async (
  servicePath: string,
  isWebsocket?: boolean,
  ignoreNeverProxy: boolean = false
) => {
  const sessionInfo = await session.getInfo();

  if (shouldUseGatewayAuth(sessionInfo.platform) && (!isNeverProxyPath(servicePath) || ignoreNeverProxy)) {
    if (DevsiteEnv.LOCAL_DEV_GATEWAY_PROXY) {
      return `${getDevsiteFullPath(gatewayApiPath)}${servicePath}`;
    }
    const gatewayOrigin = getSubdomainServiceUrl(
      ExtendSubdomainService.DEVTOOLS_SERVER,
      DevsiteEnv.SUBDOMAIN_API_BASE_URL,
      sessionInfo.regionAlias && !DevsiteEnv.OMIT_REGION_ALIAS ? sessionInfo.regionAlias : undefined
    );
    const requestUrl = new URL(servicePath, gatewayOrigin);
    if (isWebsocket) {
      requestUrl.protocol = 'wss:';
    }
    // An empty path (ROBOT) serializes as `https://host/`. Callers append absolute paths the same
    // way they append to ROBOT_URL (`${robotURL}/observability/...`), so the extra slash becomes
    // `host//observability` and the gateway rejects the CORS preflight.
    if (!servicePath) {
      return requestUrl.origin;
    }
    return requestUrl.href;
  }

  // always proxy requests if backend for frontend is turned on. Don't proxy websockets.
  const shouldProxy = BFF_ENABLED && !isWebsocket && (!isNeverProxyPath(servicePath) || ignoreNeverProxy);

  const { scope, authBaseUri } = sessionInfo;
  if (!shouldProxy && !isWebsocket && servicePath === ExtendServicePath.OCTOPAAS && authBaseUri) {
    return authBaseUri;
  }

  const gateway = shouldProxy ? getDevsiteFullPath(apiPath) : getBaseUrl(scope.region_fqdn, isWebsocket);
  return `${gateway}${servicePath}`;
};
